import { Select } from '@/components/Select'
import React from 'react'
import { faTrashAlt } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { ActionMeta, components, OnChangeValue } from 'react-select'

import './ProfileSelector.css'
import ProfileCreateDialog from './ProfileCreateDialog'
import { SimulationProfile } from '@/api/types'
import { toast } from 'react-toastify'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  deleteSimulationProfile,
  saveSimulationProfile,
  simulationProfilesQuery,
  updateSimulationProfile,
} from '@/api/mealPlan'
import { useRouter } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

const customStyles = {
  control: (baseStyles) => ({
    ...baseStyles,
    width: '160px',
    marginLeft: '5px',
  }),
  option: (provided, state) => ({
    ...provided,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: state.isSelected ? '#f0f8e3' : 'white',
    color: 'black',
    padding: '10px',
    ':hover': {
      backgroundColor: '#f0f8e3',
    },
  }),
  menuList: (provided) => ({
    ...provided,
    borderRadius: '5px',
  }),
  divider: {
    height: '1px',
    background: '#ddd',
    margin: '8px 0',
  },
}

function CustomOption(props: any) {
  return (
    <components.Option {...props}>
      {props.children}
      <button
        className="delete-icon"
        onClick={(ev) => {
          ev.stopPropagation()
          props.onDelete(props.data.value)
        }}>
        <FontAwesomeIcon icon={faTrashAlt} size="sm" />
      </button>
    </components.Option>
  )
}

const CustomMenuList = (props) => {
  const { t } = useTranslation()
  return (
    <components.MenuList {...props}>
      {props.children}
      <div style={customStyles.divider} />
      <div className="save-option" onClick={props.onSaveProfile}>
        {t('simulationProfiles.saveProfile')}
      </div>
    </components.MenuList>
  )
}

type Props = {
  simulationProfile: SimulationProfile
  simulationProfiles: SimulationProfile[]
  onChange(profile: SimulationProfile): void
}

type Option = {
  value: number
  label: string
}

export default function ProfileSelector({
  simulationProfiles,
  simulationProfile,
  onChange,
}: Props) {
  let [isOpen, setOpen] = React.useState(false)
  const router = useRouter()
  const queryClient = useQueryClient()

  function handleChangeProfile(
    newValue: OnChangeValue<Option, false>,
    actionMeta: ActionMeta<Option>,
  ): void {
    if (actionMeta.action === 'select-option' && newValue) {
      const profile = simulationProfiles.find((p) => p.id === newValue.value)
      if (profile) {
        onChange(profile)
      }
    } else if (actionMeta.action === 'clear') {
      onChange({ userProfile: simulationProfile.userProfile })
    }
  }

  const createProfileMutation = useMutation({
    mutationFn: (formData: SimulationProfile) => saveSimulationProfile(formData),
    onSuccess: (profile) => {
      onChange({ ...simulationProfile, ...profile })

      queryClient.setQueriesData(simulationProfilesQuery, (oldData: SimulationProfile[]) => {
        return [...oldData, profile]
      })
      router.invalidate()

      toast.success('Profile created')
    },
    onError: (error) => {
      toast.error('Error creating profile')
    },
  })

  const updateProfileMutation = useMutation({
    mutationFn: (profile: SimulationProfile) => updateSimulationProfile(profile),
    onSuccess: (profile) => {
      onChange({ ...simulationProfile, ...profile })
      toast.success('Profile updated')
    },
    onError: (error) => {
      toast.error('Error updating profile')
    },
  })

  const deleteProfileMutation = useMutation({
    mutationFn: (profileId: number) => deleteSimulationProfile(profileId),
    onSuccess: (response, profileId) => {
      if (simulationProfile.id === profileId) {
        onChange({ userProfile: simulationProfile.userProfile, id: undefined, name: undefined })
      }

      queryClient.setQueriesData(simulationProfilesQuery, (oldData: SimulationProfile[]) => {
        return oldData.filter((item: SimulationProfile) => item.id !== profileId)
      })
      router.invalidate()

      toast.success('Profile deleted')
    },
    onError: (error) => {
      toast.error('Error deleting profile')
    },
  })

  return (
    <>
      <Select
        isSearchable={false}
        isClearable={true}
        value={{ value: simulationProfile.id, label: simulationProfile.name }}
        options={simulationProfiles.map((profile) => ({
          value: profile.id,
          label: profile.name,
        }))}
        components={{
          Option: (props) => (
            <CustomOption
              {...props}
              onDelete={(value: number) => {
                deleteProfileMutation.mutate(value)
              }}
            />
          ),
          MenuList: (props) => (
            <CustomMenuList
              {...props}
              simulationProfile={simulationProfile}
              onSaveProfile={() =>
                simulationProfile.id
                  ? updateProfileMutation.mutate(simulationProfile)
                  : setOpen(true)
              }
            />
          ),
        }}
        onChange={handleChangeProfile}
        styles={customStyles}
      />

      <ProfileCreateDialog
        simulationProfile={simulationProfile}
        onSaveProfile={(profile, close) =>
          createProfileMutation.mutate(profile, {
            onSuccess: () => {
              close()
            },
          })
        }
        isOpen={isOpen}
        setIsOpen={setOpen}
      />
    </>
  )
}
